import { injectable } from 'tsyringe';
import { BaseViewModel } from '@/shared/viewmodels/BaseViewModel';
import { analyticsFiltersEqual, mergeAnalyticsFilters } from '@/shared/analytics/AnalyticsFilterOps';
import type { AnalyticsField, AnalyticsFilter } from '@/shared/analytics/AnalyticsTypes';

/**
 * Holds the active assist filters for the surface. The assist chart drill-ins
 * push filters in; the page's view models subscribe and re-query. Registered as
 * a singleton so the filters persist across drill-in navigation (gallery →
 * topic → evidence). The active array keeps a stable identity between changes so
 * dependent effects only fire on a real change.
 */
@injectable()
export class AssistSurfaceController extends BaseViewModel {
    private filters: AnalyticsFilter[] = [];
    private surfaceId: string | null = null;

    get activeFilters(): AnalyticsFilter[] {
        return this.filters;
    }

    /**
     * Records the active surface, clearing filters when the surface root changes.
     * Filters are scoped to a surface's drill-in chain (gallery → topic →
     * evidence), not shared across different surfaces, so this is a no-op while
     * navigating within one surface and resets when moving between surfaces.
     */
    setSurface(surfaceId: string): void {
        if (this.surfaceId === surfaceId) return;
        this.surfaceId = surfaceId;
        if (this.filters.length > 0) {
            this.filters = [];
            this.notifyChange();
        }
    }

    addFilter(field: AnalyticsField, value: string): void {
        this.addFilters([{ field, values: [value] }]);
    }

    addFilters(incoming: AnalyticsFilter[]): void {
        const { filters, changed } = mergeAnalyticsFilters(this.filters, incoming);
        if (changed) {
            this.filters = filters;
            this.notifyChange();
        }
    }

    removeValue(field: AnalyticsField, value: string): void {
        const next = this.filters
            .map((filter) => (filter.field === field ? { field, values: filter.values.filter((entry) => entry !== value) } : filter))
            .filter((filter) => filter.values.length > 0);
        this.filters = next;
        this.notifyChange();
    }

    clear(): void {
        if (this.filters.length === 0) return;
        this.filters = [];
        this.notifyChange();
    }

    /** Replaces the whole active-filter set in one shot (used to hydrate from the URL). No-op when equal. */
    replaceAll(filters: AnalyticsFilter[]): void {
        const next = filters.map((filter) => ({ field: filter.field, values: [...filter.values] }));
        if (analyticsFiltersEqual(this.filters, next)) return;
        this.filters = next;
        this.notifyChange();
    }

    /**
     * Re-emits the active filters with a fresh array identity, without changing
     * their contents. Used to make surface view models re-query when an external
     * dependency they fold in — the global filters (date range, provider, person)
     * — changes.
     */
    touch(): void {
        this.filters = this.filters.map((filter) => ({ field: filter.field, values: [...filter.values] }));
        this.notifyChange();
    }
}
